import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { AdminCustomerItem, AdminCustomerSummary, AdminCustomerDetailResult, Order, Deposit } from '../../types';
import { OperatorLogo } from '../../components/common/BrandLogo';
import {
  Users,
  Search,
  RefreshCw,
  Coins,
  ShoppingBag,
  UserCheck,
  ChevronDown,
  Copy,
  Check,
  ExternalLink,
  Eye,
  X,
  Clock,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Phone,
  Wallet as WalletIcon,
  Ban,
  ShieldAlert,
  UserX,
} from 'lucide-react';

export const AdminCustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<AdminCustomerItem[]>([]);
  const [summary, setSummary] = useState<AdminCustomerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'orders' | 'balance' | 'banned'>('all');
  const [activeSort, setActiveSort] = useState<'recent' | 'spent' | 'balance' | 'orders'>('recent');

  // Customer detail modal state
  const [selectedCustomer, setSelectedCustomer] = useState<AdminCustomerItem | null>(null);
  const [detailData, setDetailData] = useState<AdminCustomerDetailResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Ban / Unban modal state
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReasonInput, setBanReasonInput] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.getAdminCustomers({
        search: searchQuery.trim() || undefined,
        sort: activeSort,
        banned: activeFilter === 'banned' ? true : undefined,
      });
      setCustomers(res.customers || []);
      setSummary(res.summary || null);
    } catch (err) {
      console.error('Failed to load customers', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCustomers();
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery, activeSort, activeFilter]);

  const handleOpenDetail = async (customer: AdminCustomerItem) => {
    setSelectedCustomer(customer);
    setDetailLoading(true);
    try {
      const data = await api.getAdminCustomerDetail(customer.id);
      setDetailData(data);
    } catch (err) {
      console.error('Failed to load customer detail', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setSelectedCustomer(null);
    setDetailData(null);
    setShowBanModal(false);
    setBanReasonInput('');
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleOpenBanModal = () => {
    setBanReasonInput('ငွေလွှဲပြေစာ စလစ်အတု တင်ခြင်းကြောင့်');
    setShowBanModal(true);
  };

  const handleConfirmBan = async () => {
    if (!selectedCustomer) return;
    setActionSubmitting(true);
    try {
      const reason = banReasonInput.trim() || 'စနစ်စည်းကမ်း ဖောက်ဖျက်မှုကြောင့်';
      await api.banCustomer(selectedCustomer.id, reason);
      const updated = {
        ...selectedCustomer,
        is_banned: 1,
        ban_reason: reason,
        banned_at: new Date().toISOString(),
      };
      setSelectedCustomer(updated);
      setShowBanModal(false);
      setBanReasonInput('');
      await loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Customer အား ပိတ်ပင်၍ မရပါ');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleConfirmUnban = async () => {
    if (!selectedCustomer) return;
    if (!confirm(`${selectedCustomer.name} ၏ အကောင့်အား ပြန်လည်ဖွင့်လှစ်ပေးရန် သေချာပါသလား?`)) return;
    setActionSubmitting(true);
    try {
      await api.unbanCustomer(selectedCustomer.id);
      const updated = {
        ...selectedCustomer,
        is_banned: 0,
        ban_reason: null,
        banned_at: null,
      };
      setSelectedCustomer(updated);
      await loadCustomers();
    } catch (err: any) {
      alert(err.message || 'Customer အကောင့် ပြန်ဖွင့်၍ မရပါ');
    } finally {
      setActionSubmitting(false);
    }
  };

  // Client-side quick filter on active subset
  const filteredCustomers = customers.filter((c) => {
    if (activeFilter === 'orders') return c.completed_orders_count > 0;
    if (activeFilter === 'balance') return c.wallet_balance > 0;
    if (activeFilter === 'banned') return c.is_banned === 1;
    return true;
  });

  const formatKs = (val: number) => `${Math.round(val).toLocaleString()} Ks`;

  return (
    <div className="space-y-4 pb-20 animate-fadeIn">
      {/* 1. Header Card */}
      <div className="relative overflow-hidden bg-gradient-to-br from-surface-card via-surface-card to-slate-900 border border-plum-border/80 rounded-2xl p-4 sm:p-5 shadow-lg">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center text-primary shadow-xs">
              <Users className="w-4 h-4" />
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-slate-100 tracking-tight">
              သုံးစွဲသူ စာရင်း
            </h1>
          </div>

          <button
            onClick={loadCustomers}
            disabled={loading}
            className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 border border-slate-700/60 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
            title="အသစ်ပြန်တင်မည်"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
          </button>
        </div>

        {/* Quick KPI Overview */}
        {summary && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2.5 pt-3 border-t border-slate-800/80">
            <div className="bg-slate-900/60 rounded-xl p-2.5 border border-slate-800">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                <Users className="w-3 h-3 text-primary" /> စုစုပေါင်း
              </span>
              <p className="text-base font-extrabold text-slate-100 mt-1">
                {summary.totalCustomersCount.toLocaleString()}
              </p>
            </div>

            <div className="bg-slate-900/60 rounded-xl p-2.5 border border-slate-800">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                <UserCheck className="w-3 h-3 text-emerald-400" /> အော်ဒါဝယ်သူ
              </span>
              <p className="text-base font-extrabold text-emerald-400 mt-1">
                {summary.activeCustomersCount.toLocaleString()}
              </p>
            </div>

            <div className="bg-slate-900/60 rounded-xl p-2.5 border border-slate-800">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                <Coins className="w-3 h-3 text-amber-400" /> Wallet စုစုပေါင်း
              </span>
              <p className="text-base font-extrabold text-amber-300 mt-1">
                {formatKs(summary.totalWalletBalanceLiability)}
              </p>
            </div>

            <div className="bg-slate-900/60 rounded-xl p-2.5 border border-slate-800">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1">
                <ShoppingBag className="w-3 h-3 text-sky-400" /> ရောင်းရငွေ
              </span>
              <p className="text-base font-extrabold text-sky-300 mt-1">
                {formatKs(summary.totalCustomerSpend)}
              </p>
            </div>

            <div className="bg-slate-900/60 rounded-xl p-2.5 border border-slate-800 col-span-2 sm:col-span-1">
              <span className="text-[10px] uppercase font-bold text-red-400 tracking-wider flex items-center gap-1">
                <Ban className="w-3 h-3 text-red-400" /> ပိတ်ထားသူများ
              </span>
              <p className="text-base font-extrabold text-red-400 mt-1">
                {(summary.bannedCustomersCount || 0).toLocaleString()}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 2. Search & Filter Bar */}
      <div className="space-y-2.5">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="အမည်၊ @username၊ Telegram ID သို့မဟုတ် ဖုန်းနံပါတ်ဖြင့် ရှာဖွေပါ..."
            className="w-full pl-10 pr-9 py-2.5 bg-surface-card border border-plum-border rounded-xl text-xs sm:text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all shadow-inner"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 p-0.5 rounded-md"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Pills & Sort Selector */}
        <div className="flex items-center justify-between gap-2 overflow-x-auto pb-1 no-scrollbar text-xs">
          <div className="flex items-center gap-1.5 flex-nowrap">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeFilter === 'all'
                  ? 'bg-primary text-white shadow-xs'
                  : 'bg-surface-card text-slate-400 hover:text-slate-200 border border-plum-border/60'
              }`}
            >
              အားလုံး ({customers.length})
            </button>
            <button
              onClick={() => setActiveFilter('orders')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeFilter === 'orders'
                  ? 'bg-primary text-white shadow-xs'
                  : 'bg-surface-card text-slate-400 hover:text-slate-200 border border-plum-border/60'
              }`}
            >
              အော်ဒါရှိသူများ
            </button>
            <button
              onClick={() => setActiveFilter('balance')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeFilter === 'balance'
                  ? 'bg-primary text-white shadow-xs'
                  : 'bg-surface-card text-slate-400 hover:text-slate-200 border border-plum-border/60'
              }`}
            >
              လက်ကျန်ငွေရှိသူများ
            </button>
            <button
              onClick={() => setActiveFilter('banned')}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all whitespace-nowrap flex items-center gap-1 ${
                activeFilter === 'banned'
                  ? 'bg-red-600 text-white shadow-xs'
                  : 'bg-surface-card text-red-400 hover:text-red-300 border border-red-500/30'
              }`}
            >
              <Ban className="w-3 h-3" />
              <span>ပိတ်ထားသူများ ({customers.filter((c) => c.is_banned === 1).length})</span>
            </button>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[11px] text-slate-500 hidden sm:inline">စဥ်မည်:</span>
            <select
              value={activeSort}
              onChange={(e) => setActiveSort(e.target.value as any)}
              aria-label="Sort customers"
              className="bg-surface-card text-slate-300 text-xs border border-plum-border rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-primary"
            >
              <option value="recent">အသစ်ဆုံး အရင်</option>
              <option value="spent">အသုံးများဆုံး</option>
              <option value="balance">လက်ကျန်ငွေ အများဆုံး</option>
              <option value="orders">အော်ဒါ အများဆုံး</option>
            </select>
          </div>
        </div>
      </div>

      {/* 3. Customer List */}
      <div className="space-y-2.5">
        {loading && customers.length === 0 ? (
          <div className="py-12 text-center text-slate-500 flex flex-col items-center gap-2">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            <p className="text-xs">Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="py-12 text-center bg-surface-card/40 border border-plum-border/60 rounded-2xl p-6">
            <Users className="w-10 h-10 text-slate-600 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-300">သုံးစွဲသူ ရှာမတွေ့ပါ</p>
            <p className="text-xs text-slate-500 mt-1">
              {searchQuery ? `"${searchQuery}" နှင့် ကိုက်ညီသော အကောင့် မရှိပါ။` : 'ဤကဏ္ဍတွင် သုံးစွဲသူ မရှိပါ။'}
            </p>
          </div>
        ) : (
          filteredCustomers.map((cust) => {
            const isBanned = cust.is_banned === 1;
            const initials = (cust.name || 'C')
              .split(' ')
              .map((n) => n[0])
              .slice(0, 2)
              .join('')
              .toUpperCase();

            return (
              <div
                key={cust.id}
                onClick={() => handleOpenDetail(cust)}
                className={`border transition-all rounded-xl p-3.5 sm:p-4 shadow-sm hover:shadow-md cursor-pointer group ${
                  isBanned
                    ? 'bg-red-950/15 border-red-500/40 hover:border-red-500'
                    : 'bg-surface-card border-plum-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  {/* Left: Avatar & Info */}
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-full border flex items-center justify-center font-bold text-xs shrink-0 shadow-inner ${
                        isBanned
                          ? 'bg-red-900/40 border-red-500 text-red-300'
                          : 'bg-gradient-to-br from-primary/30 to-purple-800/40 border-primary/40 text-primary-light'
                      }`}
                    >
                      {isBanned ? <Ban className="w-4 h-4 text-red-400" /> : initials}
                    </div>

                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-slate-100 truncate group-hover:text-primary transition-colors">
                          {cust.name}
                        </h3>

                        {isBanned ? (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-300 bg-red-900/40 border border-red-500/40 px-2 py-0.5 rounded-md">
                            <Ban className="w-3 h-3 text-red-400" />
                            <span>အကောင့်ပိတ်ထား</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-1.5 py-0.2 rounded-md">
                            <span>🟢 ပုံမှန်</span>
                          </span>
                        )}

                        {cust.username && (
                          <a
                            href={`https://t.me/${cust.username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-0.5 text-[11px] font-medium text-sky-400 bg-sky-950/60 hover:bg-sky-900/80 px-1.5 py-0.5 rounded border border-sky-800/60 transition-colors"
                          >
                            @{cust.username}
                            <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                          </a>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-[11px] text-slate-400 flex-wrap">
                        {cust.telegram_id && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(cust.telegram_id!, `tg-${cust.id}`);
                            }}
                            className="inline-flex items-center gap-1 font-mono hover:text-slate-200 transition-colors cursor-pointer"
                            title="Telegram ID ကူးယူရန်"
                          >
                            TG: {cust.telegram_id}
                            {copiedId === `tg-${cust.id}` ? (
                              <Check className="w-3 h-3 text-emerald-400" />
                            ) : (
                              <Copy className="w-3 h-3 opacity-60" />
                            )}
                          </span>
                        )}

                        {cust.phone && (
                          <span className="font-mono text-slate-400">
                            • {cust.phone}
                          </span>
                        )}
                      </div>

                      {isBanned && cust.ban_reason && (
                        <p className="text-[11px] text-red-300/90 font-medium">
                          ⚠️ အကြောင်းရင်း: <i>{cust.ban_reason}</i>
                        </p>
                      )}

                      <p className="text-[10px] text-slate-500">
                        Joined {cust.created_at ? new Date(cust.created_at).toLocaleDateString() : 'N/A'}
                        {cust.last_order_at && (
                          <span> • Last order: {new Date(cust.last_order_at).toLocaleDateString()}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Right: Metrics & Action */}
                  <div className="text-right shrink-0 space-y-1">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-slate-500 block">Balance</span>
                      <span className={`text-xs sm:text-sm font-extrabold ${cust.wallet_balance > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>
                        {formatKs(cust.wallet_balance)}
                      </span>
                    </div>

                    <div className="text-[10px] text-slate-400">
                      <span className="font-bold text-slate-200">{cust.completed_orders_count}</span> orders (
                      <span className="text-sky-300 font-semibold">{formatKs(cust.total_spent)}</span>)
                    </div>

                    <div className="pt-0.5">
                      <span className="inline-flex items-center gap-1 text-[10px] text-primary group-hover:underline">
                        <Eye className="w-3 h-3" /> View History
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 4. Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div
            className="bg-surface-card border border-plum-border rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-scaleUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-plum-border flex items-start justify-between bg-surface-header">
              <div className="flex items-center gap-3">
                <div
                  className={`w-11 h-11 rounded-full border flex items-center justify-center font-bold text-sm shadow-inner ${
                    selectedCustomer.is_banned === 1
                      ? 'bg-red-900/40 border-red-500 text-red-300'
                      : 'bg-gradient-to-br from-primary/30 to-purple-800/40 border-primary/40 text-primary-light'
                  }`}
                >
                  {selectedCustomer.is_banned === 1 ? (
                    <Ban className="w-5 h-5 text-red-400" />
                  ) : (
                    (selectedCustomer.name || 'C').slice(0, 2).toUpperCase()
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base sm:text-lg font-bold text-slate-100">
                      {selectedCustomer.name}
                    </h2>
                    {selectedCustomer.is_banned === 1 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-300 bg-red-900/40 border border-red-500/40 px-2 py-0.5 rounded-md">
                        <Ban className="w-3 h-3" /> ပိတ်ထား
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                    {selectedCustomer.username && (
                      <a
                        href={`https://t.me/${selectedCustomer.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-400 hover:underline flex items-center gap-0.5"
                      >
                        @{selectedCustomer.username}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {selectedCustomer.telegram_id && (
                      <span className="font-mono">TG ID: {selectedCustomer.telegram_id}</span>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={handleCloseDetail}
                className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-slate-100 hover:bg-slate-700 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-5 overflow-y-auto space-y-4 text-xs">
              {/* Banned Alert Banner */}
              {selectedCustomer.is_banned === 1 && (
                <div className="bg-red-950/40 border border-red-500/50 rounded-xl p-3 text-red-300 flex items-start gap-2.5">
                  <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-xs text-red-200">
                      ဤအကောင့်အား ယာယီပိတ်ပင်ထားပါသည် (Account Banned)
                    </p>
                    <p className="text-[11px] text-red-300">
                      <b>အကြောင်းရင်း:</b> {selectedCustomer.ban_reason || 'မဖော်ပြထားပါ'}
                    </p>
                    {selectedCustomer.banned_at && (
                      <p className="text-[10px] text-red-400/80">
                        ပိတ်သည့်ရက်: {new Date(selectedCustomer.banned_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Wallet & Spending Snapshot */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-3">
                  <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1">
                    <WalletIcon className="w-3 h-3 text-emerald-400" /> Wallet Balance
                  </span>
                  <p className="text-base font-extrabold text-emerald-400 mt-1">
                    {formatKs(selectedCustomer.wallet_balance)}
                  </p>
                  {selectedCustomer.wallet_reserved > 0 && (
                    <p className="text-[10px] text-amber-400 mt-0.5">
                      ({formatKs(selectedCustomer.wallet_reserved)} reserved)
                    </p>
                  )}
                </div>

                <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-3">
                  <span className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1">
                    <ShoppingBag className="w-3 h-3 text-sky-400" /> Total Spend
                  </span>
                  <p className="text-base font-extrabold text-sky-300 mt-1">
                    {formatKs(selectedCustomer.total_spent)}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {selectedCustomer.completed_orders_count} orders completed
                  </p>
                </div>
              </div>

              {/* Direct Telegram Action */}
              {selectedCustomer.username && (
                <a
                  href={`https://t.me/${selectedCustomer.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2 px-3 rounded-xl bg-sky-600/20 border border-sky-500/40 text-sky-300 font-semibold flex items-center justify-center gap-2 hover:bg-sky-600/30 transition-all text-xs"
                >
                  <span>Chat with @{selectedCustomer.username} on Telegram</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}

              {/* Orders History Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-slate-200 text-xs flex items-center gap-1.5">
                    <ShoppingBag className="w-3.5 h-3.5 text-primary" /> Recent Orders
                  </h4>
                  <span className="text-[10px] text-slate-500">
                    {detailData?.orders ? `${detailData.orders.length} orders` : 'Loading...'}
                  </span>
                </div>

                {detailLoading ? (
                  <div className="py-6 text-center text-slate-500">
                    <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1 text-primary" />
                    <span>Loading history...</span>
                  </div>
                ) : detailData?.orders && detailData.orders.length > 0 ? (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {detailData.orders.map((ord) => (
                      <div
                        key={ord.id}
                        className="bg-slate-900/50 border border-slate-800 rounded-lg p-2.5 flex items-center justify-between gap-2 text-[11px]"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <OperatorLogo id={ord.operator_id} size="sm" />
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-200 truncate">
                              {ord.order_type === 'package' ? ord.package_id || 'Data Package' : 'Phone Bill'}
                            </p>
                            <p className="text-[10px] text-slate-400 font-mono">
                              {ord.recipient_phone} • {ord.created_at ? new Date(ord.created_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className="font-bold text-slate-100">{formatKs(ord.amount)}</span>
                          <span
                            className={`block text-[9px] uppercase font-bold ${
                              ord.fulfillment_status === 'completed'
                                ? 'text-emerald-400'
                                : ord.fulfillment_status === 'unable_to_deliver'
                                ? 'text-red-400'
                                : 'text-amber-400'
                            }`}
                          >
                            {ord.fulfillment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 py-3 text-center bg-slate-900/30 rounded-lg border border-slate-800/40">
                    No orders placed yet.
                  </p>
                )}
              </div>

              {/* Deposits History Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-slate-200 text-xs flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5 text-amber-400" /> Recent Deposits
                  </h4>
                  <span className="text-[10px] text-slate-500">
                    {detailData?.deposits ? `${detailData.deposits.length} deposits` : 'Loading...'}
                  </span>
                </div>

                {detailLoading ? (
                  <div className="py-4 text-center text-slate-500">
                    <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1 text-primary" />
                    <span>Loading deposits...</span>
                  </div>
                ) : detailData?.deposits && detailData.deposits.length > 0 ? (
                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                    {detailData.deposits.map((dep) => (
                      <div
                        key={dep.id}
                        className="bg-slate-900/50 border border-slate-800 rounded-lg p-2.5 flex items-center justify-between gap-2 text-[11px]"
                      >
                        <div>
                          <p className="font-semibold text-slate-200 uppercase">
                            {dep.payment_method} Deposit
                          </p>
                          <p className="text-[10px] text-slate-400 font-mono">
                            {dep.created_at ? new Date(dep.created_at).toLocaleDateString() : ''}
                            {dep.tx_ref ? ` • Ref: ${dep.tx_ref}` : ''}
                          </p>
                        </div>

                        <div className="text-right shrink-0">
                          <span className="font-bold text-emerald-400">+{formatKs(dep.amount)}</span>
                          <span
                            className={`block text-[9px] uppercase font-bold ${
                              dep.status === 'credited'
                                ? 'text-emerald-400'
                                : dep.status === 'rejected'
                                ? 'text-red-400'
                                : 'text-amber-400'
                            }`}
                          >
                            {dep.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-500 py-3 text-center bg-slate-900/30 rounded-lg border border-slate-800/40">
                    No deposits recorded yet.
                  </p>
                )}
              </div>
            </div>

            {/* Modal Footer with Ban / Unban Actions */}
            <div className="p-3.5 bg-surface-header border-t border-plum-border flex items-center justify-between gap-2">
              <div>
                {selectedCustomer.is_banned === 1 ? (
                  <button
                    type="button"
                    disabled={actionSubmitting}
                    onClick={handleConfirmUnban}
                    className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-semibold text-xs transition flex items-center gap-1.5 shadow-xs"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>အကောင့်ပြန်ဖွင့်မည် (Unban)</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={actionSubmitting}
                    onClick={handleOpenBanModal}
                    className="px-3.5 py-1.5 rounded-xl bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/40 active:scale-95 font-semibold text-xs transition flex items-center gap-1.5"
                  >
                    <Ban className="w-3.5 h-3.5 text-red-400" />
                    <span>အကောင့်ပိတ်မည် (Ban)</span>
                  </button>
                )}
              </div>

              <button
                onClick={handleCloseDetail}
                className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs transition"
              >
                ပိတ်မည် (Close)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 5. Ban Confirmation Modal */}
      {showBanModal && selectedCustomer && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-surface-card border border-red-500/40 rounded-2xl w-full max-w-sm p-4 sm:p-5 shadow-2xl space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-plum-border">
              <h3 className="font-bold text-red-400 text-base flex items-center gap-1.5">
                <Ban className="w-4 h-4 text-red-400" />
                <span>Customer အကောင့် ပိတ်ပင်ရန်</span>
              </h3>
              <button
                onClick={() => setShowBanModal(false)}
                className="p-1 rounded-full text-slate-400 hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-800 text-xs space-y-1">
              <p className="text-slate-300 font-semibold">
                Customer: <span className="text-slate-100 font-bold">{selectedCustomer.name}</span>
              </p>
              {selectedCustomer.telegram_id && (
                <p className="text-slate-400 font-mono text-[11px]">
                  TG ID: {selectedCustomer.telegram_id}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                ပိတ်ပင်ရသည့် အကြောင်းရင်း (Reason)
              </label>
              <textarea
                rows={3}
                value={banReasonInput}
                onChange={(e) => setBanReasonInput(e.target.value)}
                placeholder="ဥပမာ: ငွေလွှဲပြေစာ စလစ်အတု တင်ခြင်းကြောင့်"
                className="w-full bg-surface-page border border-plum-border rounded-xl p-2.5 text-xs text-slate-100 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />

              {/* Quick Suggestion Chips */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[
                  'ငွေလွှဲစလစ်အတု တင်ခြင်း',
                  'မူမမှန်သော လုပ်ဆောင်ချက်',
                  'စည်းကမ်းဖောက်ဖျက်မှု',
                ].map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setBanReasonInput(chip)}
                    className="text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-0.5 rounded-md border border-slate-700 transition"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setShowBanModal(false)}
                className="flex-1 border border-plum-border py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-slate-200"
              >
                မလုပ်တော့ပါ
              </button>
              <button
                type="button"
                disabled={actionSubmitting}
                onClick={handleConfirmBan}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white py-2.5 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 shadow-sm active:scale-98"
              >
                <Ban className="w-3.5 h-3.5" />
                <span>{actionSubmitting ? 'ပိတ်နေပါသည်...' : 'အတည်ပြုပိတ်မည်'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
